// Singles Chain — useSinglesChain hook.
//
// Reads the user's chains + provides startChain action.
// Chains are per-session, so this returns chains for the current
// session plus recent history.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface ScChain {
  id: string;
  session_id: string;
  chain_label: string;
  opening_stake: number;
  g1_stake: number | null;
  g1_return: number | null;
  g2_stake: number | null;
  g2_return: number | null;
  g3_stake: number | null;
  g3_return: number | null;
  day_profit: number | null;
  day_result: "win" | "loss" | "partial" | "pending" | null;
  bust_at_game: number | null;
  cooldown_ends_at: string | null;
  status: "active" | "settled_win" | "settled_loss" | "busted" | "skipped";
  stake_source?: "capital" | "profit" | null;
  created_at: string;
}

/**
 * Slice ζ — structured cooldown response from singles-start-chain edge fn
 * when the 24h post-bust lockout is active. UI uses cooldown_ends_at to
 * render an honest countdown card instead of a generic error toast.
 */
export interface ScCooldownInfo {
  cooldown_ends_at: string; // ISO8601 UTC
  retry_after_seconds: number;
}

/**
 * Slice κ-UI — structured error result from singles-play-game when
 * the per-slot stake confirmation hits one of the PRD §10 mappings.
 * UI can switch on code to render the right surface (cooldown card,
 * insufficient-profit prompt, reveal countdown, source-mismatch hint).
 */
export interface ScPlayGameError {
  code: "SC_COOLDOWN" | "SC_GAME_NOT_REVEALED" | "SC_INVALID_STAKE_SOURCE" | "SC_INSUFFICIENT_PROFIT" | "SC_INVALID_GAME_NUMBER" | "RPC_ERROR";
  message: string;
  cooldown_ends_at?: string;
  retry_after_seconds?: number;
  reveal_at?: string;
  profit_balance?: number;
  stake_required?: number;
}

export interface UseSinglesChainState {
  chains: readonly ScChain[];
  activeChain: ScChain | null;
  loading: boolean;
  error: string | null;
  cooldown: ScCooldownInfo | null;
  playGameError: ScPlayGameError | null;
  startChain: (sessionId: string, stake: number, label?: string) => Promise<boolean>;
  playGame: (chainId: string, gameNumber: 1 | 2 | 3, stakeAmount: number, stakeSource: "capital" | "profit") => Promise<boolean>;
  refetch: () => void;
}

// ─── Parser ───────────────────────────────────────────────

function parseChain(row: Record<string, unknown>): ScChain {
  return {
    id: String(row.id ?? ""),
    session_id: String(row.session_id ?? ""),
    chain_label: String(row.chain_label ?? "default"),
    opening_stake: Number(row.opening_stake ?? 0),
    g1_stake: typeof row.g1_stake === "number" ? row.g1_stake : null,
    g1_return: typeof row.g1_return === "number" ? row.g1_return : null,
    g2_stake: typeof row.g2_stake === "number" ? row.g2_stake : null,
    g2_return: typeof row.g2_return === "number" ? row.g2_return : null,
    g3_stake: typeof row.g3_stake === "number" ? row.g3_stake : null,
    g3_return: typeof row.g3_return === "number" ? row.g3_return : null,
    day_profit: typeof row.day_profit === "number" ? row.day_profit : null,
    day_result: typeof row.day_result === "string"
      ? (row.day_result as ScChain["day_result"])
      : null,
    bust_at_game: typeof row.bust_at_game === "number" ? row.bust_at_game : null,
    cooldown_ends_at: typeof row.cooldown_ends_at === "string" ? row.cooldown_ends_at : null,
    status: (row.status as ScChain["status"]) ?? "active",
    stake_source: row.stake_source === "capital" || row.stake_source === "profit"
      ? row.stake_source
      : null,
    created_at: String(row.created_at ?? ""),
  };
}

// ─── Hook ─────────────────────────────────────────────────

export function useSinglesChain(): UseSinglesChainState {
  const { user } = useAuthSession();
  const [chains, setChains] = useState<readonly ScChain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState<ScCooldownInfo | null>(null);
  const [playGameError, setPlayGameError] = useState<ScPlayGameError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setChains([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: qError } = await (
          (client as unknown as {
            from: (table: string) => {
              select: (cols: string) => {
                eq: (col: string, val: string) => {
                  order: (col: string, opts: { ascending: boolean }) => {
                    limit: (n: number) => Promise<{
                      data: Record<string, unknown>[] | null;
                      error: { message: string } | null;
                    }>;
                  };
                };
              };
            };
          }).from("sc_chains")
        )
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(20);

        if (cancelled) return;

        if (qError) {
          setError(qError.message);
          setLoading(false);
          return;
        }

        const parsed = (data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parseChain);

        setChains(parsed);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  const startChain = useCallback(
    async (sessionId: string, stake: number, label = "default"): Promise<boolean> => {
      const client = getBrowserClient();
      setError(null);
      setCooldown(null);
      try {
        // Slice ζ — invoke the wrapper edge fn so cooldown comes back as
        // HTTP 423 with a structured body (cooldown_ends_at + retry_after).
        const { data, error: fnError } = await client.functions.invoke(
          "singles-start-chain",
          { body: { session_id: sessionId, stake, chain_label: label } },
        );

        if (fnError) {
          // supabase-js exposes the response body on FunctionsHttpError.context
          const ctx = (fnError as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            try {
              const parsed = await ctx.json();
              if (parsed && parsed.code === "SC_COOLDOWN" && parsed.cooldown_ends_at) {
                setCooldown({
                  cooldown_ends_at: String(parsed.cooldown_ends_at),
                  retry_after_seconds: Number(parsed.retry_after_seconds ?? 0),
                });
                setError("cooldown_active");
                return false;
              }
              setError(String(parsed?.message ?? parsed?.error ?? fnError.message));
              return false;
            } catch {
              /* fall through */
            }
          }
          setError(fnError.message);
          return false;
        }

        // Defensive: edge fn might also pass the body through `data` on 423.
        if (data && (data as Record<string, unknown>).code === "SC_COOLDOWN") {
          const d = data as { cooldown_ends_at?: string; retry_after_seconds?: number };
          if (d.cooldown_ends_at) {
            setCooldown({
              cooldown_ends_at: d.cooldown_ends_at,
              retry_after_seconds: Number(d.retry_after_seconds ?? 0),
            });
            setError("cooldown_active");
            return false;
          }
        }

        refetch();
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      }
    },
    [refetch],
  );

  /**
   * Slice κ-UI — confirms a per-slot stake via the singles-play-game
   * edge fn. Mirrors startChain's HTTP-mapping pattern so the UI gets
   * the structured 423/422/402/409 codes from PRD §10 rather than a
   * raw error string. On 201 success the chains list refetches so
   * gN_stake fields update in the UI.
   */
  const playGame = useCallback(
    async (
      chainId: string,
      gameNumber: 1 | 2 | 3,
      stakeAmount: number,
      stakeSource: "capital" | "profit",
    ): Promise<boolean> => {
      const client = getBrowserClient();
      setError(null);
      setPlayGameError(null);
      try {
        const { data, error: fnError } = await client.functions.invoke(
          "singles-play-game",
          {
            body: {
              chain_id: chainId,
              game_number: gameNumber,
              stake_amount: stakeAmount,
              stake_source: stakeSource,
            },
          },
        );

        if (fnError) {
          const ctx = (fnError as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            try {
              const parsed = await ctx.json();
              const code = parsed?.code;
              if (
                code === "SC_COOLDOWN" ||
                code === "SC_GAME_NOT_REVEALED" ||
                code === "SC_INVALID_STAKE_SOURCE" ||
                code === "SC_INSUFFICIENT_PROFIT" ||
                code === "SC_INVALID_GAME_NUMBER"
              ) {
                setPlayGameError({
                  code,
                  message: String(parsed.message ?? parsed.error ?? "Play-game rejected"),
                  cooldown_ends_at: parsed.cooldown_ends_at,
                  retry_after_seconds: parsed.retry_after_seconds,
                  reveal_at: parsed.reveal_at,
                  profit_balance: parsed.profit_balance,
                  stake_required: parsed.stake_required,
                });
                // For cooldown, also populate the top-level cooldown state
                // so the existing CooldownCard surface activates immediately.
                if (code === "SC_COOLDOWN" && parsed.cooldown_ends_at) {
                  setCooldown({
                    cooldown_ends_at: String(parsed.cooldown_ends_at),
                    retry_after_seconds: Number(parsed.retry_after_seconds ?? 0),
                  });
                }
                return false;
              }
              setPlayGameError({
                code: "RPC_ERROR",
                message: String(parsed?.message ?? parsed?.error ?? fnError.message),
              });
              return false;
            } catch {
              /* fall through */
            }
          }
          setPlayGameError({ code: "RPC_ERROR", message: fnError.message });
          return false;
        }

        // Defensive: also handle if response came back via `data`.
        const code = (data as Record<string, unknown> | null)?.code;
        if (typeof code === "string" && code.startsWith("SC_")) {
          setPlayGameError({
            code: code as ScPlayGameError["code"],
            message: String((data as { message?: string })?.message ?? "Play-game rejected"),
          });
          return false;
        }

        refetch();
        return true;
      } catch (e) {
        setPlayGameError({ code: "RPC_ERROR", message: e instanceof Error ? e.message : String(e) });
        return false;
      }
    },
    [refetch],
  );

  const activeChain = useMemo(
    () => chains.find((c) => c.status === "active") ?? null,
    [chains],
  );

  return useMemo(
    () => ({ chains, activeChain, loading, error, cooldown, playGameError, startChain, playGame, refetch }),
    [chains, activeChain, loading, error, cooldown, playGameError, startChain, playGame, refetch],
  );
}
